"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { trackSectionView, trackScrollDepth, startPageView } from "@/lib/analytics";

// Page-level engagement instrumentation, mounted once in the locale layout so it
// covers every route, not just the home page. Several sections outside the home
// page carry data-track-section (the whole /tarieven page, for one), and while
// this lived in app/[locale]/page.tsx none of them ever reported.
//   - section_viewed: the first time each [data-track-section] element is >=30%
//     visible (so an element already on screen at load, e.g. the hero, counts).
//   - scroll_depth: once per 25/50/75/100% milestone of the page's scrollable
//     height.
//   - page_exited: once, when the visitor first leaves - navigating away, hiding
//     the tab, or closing it - carrying how long they stayed and how far they
//     got. It lives here because this effect already owns both facts.
// Keyed on pathname rather than mounted once: the layout survives client-side
// navigation, so without the key a route the visitor navigates to would never be
// scanned. One effect run == one page view's worth of events. track() itself
// no-ops the PostHog leg until consent + load, so this is consent-safe.
const SCROLL_MILESTONES = [25, 50, 75, 100] as const;

export function PageEngagement() {
  const pathname = usePathname();

  useEffect(() => {
    // --- Section views ---
    let observer: IntersectionObserver | undefined;
    const sections = document.querySelectorAll<HTMLElement>("[data-track-section]");
    if (sections.length > 0 && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries, obs) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const name = (entry.target as HTMLElement).dataset.trackSection;
            if (name) trackSectionView(name);
            obs.unobserve(entry.target);
          }
        },
        { threshold: 0.3 },
      );
      sections.forEach((el) => observer!.observe(el));
    }

    // --- Scroll depth ---
    const fired = new Set<number>();
    let deepest = 0;
    let ticking = false;
    const measure = () => {
      ticking = false;
      const scrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = (window.scrollY / scrollable) * 100;
      for (const milestone of SCROLL_MILESTONES) {
        if (pct >= milestone && !fired.has(milestone)) {
          fired.add(milestone);
          deepest = milestone;
          trackScrollDepth(milestone);
        }
      }
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(measure);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    measure(); // a short page may already be past a milestone on load

    // --- Departure ---
    //
    // Three ways to leave and one event. `visibilitychange` is the only one a
    // mobile browser reliably fires - it is what happens when the visitor
    // switches apps, and neither unload nor beforeunload can be trusted there.
    // `pagehide` covers the desktop close and the bfcache. The effect cleanup
    // covers a client-side route change, which fires neither.
    //
    // At most once, guarded by `left`: a visitor who switches tabs and comes
    // back would otherwise send an exit per switch, and the second one's dwell
    // would count time they spent somewhere else. The cost of the guard is that
    // this measures time to FIRST departure rather than total time on the page.
    // That is the honest figure to have: it is the one a bounce is made of, and
    // an "attention time" that accumulates across returns needs the visibility
    // arithmetic this deliberately does not do.
    // Opened FIRST, before any listener can fire: this is what captures the path
    // and query string of the page being measured. Reading them at departure
    // reports the route the visitor is arriving at instead, because the cleanup
    // below runs after the router has already moved.
    const reportExit = startPageView();
    // performance.now(), not Date.now(): it is monotonic. A wall clock stepped
    // backward mid-visit - NTP correcting, a laptop resuming from sleep - makes
    // Date.now() arithmetic produce a negative dwell on an ordinary visit.
    const openedAt = performance.now();
    let left = false;
    const leave = () => {
      if (left) return;
      left = true;
      reportExit(Math.round(performance.now() - openedAt), deepest);
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") leave();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", leave);

    return () => {
      observer?.disconnect();
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", leave);
      leave();
    };
  }, [pathname]);

  return null;
}
